import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase';

interface VideoPlayerProps {
  videoUrl: string;
  videoTitle: string;
  videoDescription?: string;
  videoDuration?: number;
  chapterId: string;
  onVideoComplete?: () => void;
  autoPlay?: boolean;
}

interface VideoProgress {
  watch_progress: number;
  last_position: number;
  completed: boolean;
  total_watch_time: number;
}

// Helper function to extract YouTube video ID
const extractYouTubeVideoId = (url: string): string | null => {
  const regex = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

// Helper function to extract Vimeo video ID
const extractVimeoVideoId = (url: string): string | null => {
  const regex = /(?:vimeo\.com\/)(\d+)/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

// Helper function to detect video type
const getVideoType = (url: string): 'youtube' | 'vimeo' | 'direct' => {
  if (url.includes('youtu.be/') || url.includes('youtube.com/watch')) {
    return 'youtube';
  }
  if (url.includes('vimeo.com/')) {
    return 'vimeo';
  }
  return 'direct';
};

// Cache management - limit to 500MB total
const manageCacheSize = async () => {
  try {
    if (!('caches' in window)) return;
    
    const cache = await caches.open('video-cache-v1');
    const keys = await cache.keys();
    
    if (keys.length > 10) { // Keep only last 10 videos
      console.log('🧹 Cleaning old videos from cache');
      // Remove oldest entries (FIFO)
      for (let i = 0; i < keys.length - 10; i++) {
        await cache.delete(keys[i]);
      }
    }
  } catch (error) {
    console.log('Cache management error:', error);
  }
};

// Load video with retry logic, progress tracking, and optimized for large files
const loadVideoWithRetry = async (
  video: HTMLVideoElement,
  videoUrl: string,
  cache: Cache,
  onProgress?: (progress: number) => void,
  maxRetries = 3,
  retryDelay = 2000
): Promise<void> => {
  let attempt = 0;
  
  while (attempt < maxRetries) {
    try {
      console.log(`📥 Loading video (attempt ${attempt + 1}/${maxRetries})`);
      
      // Use direct src for better browser-native progressive loading
      // Browser handles byte-range requests automatically for videos
      video.src = videoUrl;
      
      // For large videos, don't wait for full fetch - let browser handle streaming
      // The video element will handle progressive loading automatically
      console.log('✅ Video src set, browser will handle progressive loading');
      
      // Cache will be handled after video starts playing to avoid blocking
      return; // Success - browser will handle the rest
      
    } catch (error: any) {
      attempt++;
      console.warn(`⚠️ Video load attempt ${attempt} failed:`, error.message);
      
      if (attempt >= maxRetries) {
        // Final fallback: try direct src assignment
        console.log('🔄 Using direct video src as fallback');
        video.src = videoUrl;
        throw error;
      }
      
      // Wait before retrying with exponential backoff
      await new Promise(resolve => setTimeout(resolve, retryDelay * Math.pow(2, attempt)));
    }
  }
};

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  videoUrl,
  videoTitle,
  videoDescription,
  videoDuration,
  chapterId,
  onVideoComplete,
  autoPlay = false
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const loadingProgressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [videoProgress, setVideoProgress] = useState<VideoProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Detect video type
  const videoType = getVideoType(videoUrl);
  const isEmbedVideo = videoType === 'youtube' || videoType === 'vimeo';

  // Load video progress from database (skip for embed videos)
  useEffect(() => {
    if (!isEmbedVideo) {
      loadVideoProgress();
    }
  }, [chapterId, isEmbedVideo]);

  // Initialize video when component mounts
  useEffect(() => {
    // For embed videos (YouTube, Vimeo), skip loading state immediately
    if (isEmbedVideo) {
      setLoading(false);
      return;
    }

    if (videoRef.current && videoUrl) {
      // Initialize loading state immediately
      setLoading(true);
      setLoadingProgress(1); // Show 1% immediately to indicate loading has started
      
      // For large videos (30MB+), allow up to 5 minutes for initial buffering
      // This gives enough time for the browser to buffer enough data to start playback
      // Once playback starts, video will continue buffering in background
      const timeoutDuration = 300000; // 5 minutes - enough for large videos on slow connections
      
      loadingTimeoutRef.current = setTimeout(() => {
        console.warn('⚠️ Video loading timeout - taking longer than expected');
        // Don't show error immediately - video might still be buffering
        // Only show error if video still can't play after timeout
        const video = videoRef.current;
        if (video && (video.readyState < 2 || !video.buffered.length)) {
          setError('Video is taking too long to load. Please check your connection or try again.');
          setLoading(false);
        }
      }, timeoutDuration);
      
      initializeVideo();
      
      return () => {
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
        }
      };
    }
  }, [videoUrl, isEmbedVideo]);

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      if (loadingProgressIntervalRef.current) {
        clearInterval(loadingProgressIntervalRef.current);
      }
    };
  }, []);

  const loadVideoProgress = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('VideoProgress')
        .select('*')
        .eq('user_id', user.id)
        .eq('lesson_id', chapterId)
        .single();

      if (data && !error) {
        setVideoProgress(data);
        setCurrentTime(data.last_position);
        setProgress(data.watch_progress);
      }
    } catch (error) {
      console.error('Error loading video progress:', error);
    }
  };

  const saveVideoProgress = async (watchProgress: number, lastPosition: number, totalWatchTime: number, completed: boolean = false) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const progressData = {
        user_id: user.id,
        lesson_id: chapterId,
        watch_progress: Math.round(watchProgress),
        last_position: Math.round(lastPosition),
        total_watch_time: Math.round(totalWatchTime),
        completed,
        last_watched: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('VideoProgress')
        .upsert(progressData, {
          onConflict: 'user_id,lesson_id'
        });

      if (error) {
        console.error('Error saving video progress:', error);
      } else {
        setVideoProgress(prev => ({
          ...prev,
          ...progressData
        }));
      }
    } catch (error) {
      console.error('Error saving video progress:', error);
    }
  };

  const initializeVideo = async () => {
    const video = videoRef.current;
    if (!video) return;

    console.log('Initializing video with URL:', videoUrl);

    // Skip initialization for embed videos - they're handled in render
    if (isEmbedVideo) {
      setLoading(false);
      return;
    }

    // Optimize video loading for large files
    // Use 'metadata' preload for faster initial load, browser will buffer as needed
    video.preload = 'metadata';
    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
    
    // Set source directly - browser handles byte-range requests automatically
    // This is better for large videos as it allows progressive streaming
    console.log('📥 Setting video source for progressive loading');
    video.src = videoUrl;
    
    // Load metadata first
    video.load();
    
    // Start tracking loading progress immediately with polling
    // The progress event might not fire frequently enough during initial load
    const updateLoadingProgress = () => {
      if (video.buffered.length > 0 && video.duration > 0) {
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        const bufferedPercent = (bufferedEnd / video.duration) * 100;
        // Ensure progress is at least 2% if buffering has started
        const minProgress = bufferedPercent > 0 ? Math.max(bufferedPercent, 2) : 1;
        setLoadingProgress(Math.min(minProgress, 99)); // Cap at 99% until canplay
        console.log(`📊 Buffered: ${bufferedPercent.toFixed(1)}%`);
      } else if (video.readyState >= 1) {
        // If metadata is loaded but no buffered data yet, show 2% to indicate loading
        setLoadingProgress((prev) => Math.max(prev, 2));
      } else {
        // If video hasn't started loading metadata yet, ensure at least 1%
        setLoadingProgress((prev) => Math.max(prev, 1));
      }
    };
    
    // Poll progress immediately and then every 200ms
    updateLoadingProgress(); // Call immediately for initial state
    loadingProgressIntervalRef.current = setInterval(updateLoadingProgress, 200); // Check every 200ms
    
    // Try to get video from cache in background (non-blocking)
    try {
      if ('caches' in window) {
        const cache = await caches.open('video-cache-v1');
        const cachedResponse = await cache.match(videoUrl);
        
        if (cachedResponse) {
          console.log('✅ Video found in cache, will use for future loads');
          // Don't switch to cached version now - let current stream continue
          // Cache will be used automatically on next load
        } else {
          console.log('📥 Video not in cache, caching in background after playback starts');
          // Cache after video starts playing (non-blocking)
          video.addEventListener('playing', async () => {
            try {
              const fullResponse = await fetch(videoUrl);
              if (fullResponse.ok) {
                await cache.put(videoUrl, fullResponse.clone());
                console.log('✅ Video cached for future use');
                await manageCacheSize();
              }
            } catch (e) {
              console.log('⚠️ Could not cache video:', e);
            }
          }, { once: true });
        }
      }
    } catch (error) {
      console.log('Cache check error:', error);
      // Continue with normal loading
    }

    // Event listeners
    video.addEventListener('loadstart', () => {
      console.log('🎬 Video loading started');
      setLoadingProgress(2); // Set to 2% when load starts
      setLoading(true);
    });

    video.addEventListener('loadedmetadata', () => {
      console.log('Video metadata loaded, duration:', video.duration);
      
      // Show initial progress when metadata loads
      setLoadingProgress((prev) => Math.max(prev, 5)); // Show at least 5% when metadata is loaded
      
      // Wait a bit for accurate duration
      setTimeout(() => {
        if (video.duration && !isNaN(video.duration) && isFinite(video.duration)) {
          setDuration(video.duration);
          console.log('Duration set to:', video.duration);
        }
      }, 100);
      
      // Resume from last position if available
      if (videoProgress?.last_position && videoProgress.last_position > 0) {
        video.currentTime = videoProgress.last_position;
      }
    });

    video.addEventListener('progress', () => {
      // Track buffering progress for large videos
      // This event fires periodically as data loads
      if (video.buffered.length > 0 && video.duration > 0) {
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        const bufferedPercent = (bufferedEnd / video.duration) * 100;
        // Only update if less than 99% (canplay will set to 100%)
        if (bufferedPercent < 99) {
          setLoadingProgress(bufferedPercent);
        }
        console.log(`📊 Buffered: ${bufferedPercent.toFixed(1)}%`);
      } else if (video.readyState >= 1) {
        // Show some progress if metadata is loaded but no buffered data yet
        // Use a small percentage to indicate progress has started
        setLoadingProgress((prev) => Math.max(prev, 2));
      }
    });

    video.addEventListener('canplay', () => {
      console.log('Video can start playing');
      
      // Update duration if not set yet
      if (video.duration && !isNaN(video.duration) && isFinite(video.duration)) {
        setDuration(video.duration);
      }
      
      // Clear the loading progress polling interval
      if (loadingProgressIntervalRef.current) {
        clearInterval(loadingProgressIntervalRef.current);
        loadingProgressIntervalRef.current = null;
      }
      
      setLoadingProgress(100);
      setLoading(false);
      
      // Clear the loading timeout
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    });

    video.addEventListener('waiting', () => {
      // Video is buffering
      console.log('⏳ Video is buffering...');
      setLoading(true);
      // Resume progress tracking if needed
      if (!loadingProgressIntervalRef.current) {
        loadingProgressIntervalRef.current = setInterval(() => {
          const video = videoRef.current;
          if (video && video.buffered.length > 0 && video.duration > 0) {
            const bufferedEnd = video.buffered.end(video.buffered.length - 1);
            const bufferedPercent = (bufferedEnd / video.duration) * 100;
            setLoadingProgress(Math.min(bufferedPercent, 99));
          }
        }, 200);
      }
    });

    video.addEventListener('canplaythrough', () => {
      // Enough data loaded to play through without buffering
      console.log('✅ Video ready to play through');
      if (loadingProgressIntervalRef.current) {
        clearInterval(loadingProgressIntervalRef.current);
        loadingProgressIntervalRef.current = null;
      }
      setLoadingProgress(100);
      setLoading(false);
    });

    video.addEventListener('timeupdate', () => {
      const current = video.currentTime;
      const total = video.duration;
      
      if (total > 0) {
        setCurrentTime(current);
        const progressPercent = (current / total) * 100;
        setProgress(progressPercent);
      }
    });

    video.addEventListener('ended', () => {
      setIsPlaying(false);
      saveVideoProgress(100, video.duration, video.duration, true);
      onVideoComplete?.();
    });

    video.addEventListener('error', (e) => {
      console.error('Video error:', e);
      console.error('Video src:', video.src);
      setError('Failed to load video. Please check your internet connection and video URL.');
      setLoading(false);
    });

    // Auto-play if enabled
    if (autoPlay && videoProgress?.completed !== true) {
      video.play().catch(console.error);
    }
  };

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;

    const newTime = parseFloat(e.target.value);
    video.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;

    if (!document.fullscreenElement) {
      video.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Save progress periodically (every 5 seconds)
  useEffect(() => {
    if (isPlaying && currentTime > 0) {
      progressIntervalRef.current = setInterval(() => {
        const totalTime = videoProgress?.total_watch_time || 0;
        const newTotalTime = totalTime + 5; // Add 5 seconds
        saveVideoProgress(progress, currentTime, newTotalTime);
      }, 5000);
    } else {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    }

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [isPlaying, currentTime, progress, chapterId]);

  // Show/hide controls on mobile
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [isPlaying]);

  const handleVideoClick = () => {
    setShowControls(true);
    if (showControls) {
      togglePlayPause();
    }
  };

  // Don't return early for direct videos - render the video element hidden
  // This allows the video to load in the background

  if (error) {
    return (
      <div className="video-player-container error">
        <div className="video-error">
          <div className="error-icon">⚠️</div>
          <p>{error}</p>
          <button 
            className="btn-primary btn-small"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Render embed videos (YouTube, Vimeo, etc.)
  if (videoType === 'youtube') {
    const videoId = extractYouTubeVideoId(videoUrl);
    if (videoId) {
      const embedUrl = `https://www.youtube.com/embed/${videoId}`;
      
      return (
        <div className="video-player-container">
          <div className="video-wrapper youtube-wrapper">
            <iframe
              src={embedUrl}
              width="100%"
              height="100%"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ borderRadius: '12px' }}
              title={videoTitle || 'Lesson Video'}
            />
          </div>
        </div>
      );
    }
  }

  if (videoType === 'vimeo') {
    const videoId = extractVimeoVideoId(videoUrl);
    if (videoId) {
      const embedUrl = `https://player.vimeo.com/video/${videoId}`;
      
      return (
        <div className="video-player-container">
          <div className="video-wrapper youtube-wrapper">
            <iframe
              src={embedUrl}
              width="100%"
              height="100%"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              style={{ borderRadius: '12px' }}
              title={videoTitle || 'Lesson Video'}
            />
          </div>
        </div>
      );
    }
  }

  return (
    <div className="video-player-container">
      {loading && !isEmbedVideo && (
        <div className="video-loading-wrapper">
          <div className="video-loading-content">
            {/* Logo */}
            <div className="video-loading-logo">
              <img src="logo512.png" alt="Shikhun Italian" className="logo-image" />
            </div>
            
            {/* Loading Spinner with progress ring */}
            <div className="video-loading-spinner-container">
              <div className="video-loading-spinner-ring">
                <svg className="spinner-svg" viewBox="0 0 64 64">
                  <circle
                    className="spinner-circle-bg"
                    cx="32"
                    cy="32"
                    r="28"
                    fill="none"
                    strokeWidth="4"
                  />
                  <circle
                    className="spinner-circle-progress"
                    cx="32"
                    cy="32"
                    r="28"
                    fill="none"
                    strokeWidth="4"
                    strokeDasharray={`${2 * Math.PI * 28}`}
                    strokeDashoffset={`${2 * Math.PI * 28 * (1 - loadingProgress / 100)}`}
                  />
                </svg>
                <div className="spinner-percentage">
                  {Math.round(loadingProgress)}%
                </div>
              </div>
            </div>
            
            {/* Loading Text */}
            <p className="video-loading-text">
              {loadingProgress > 0 ? `Loading video... ${Math.round(loadingProgress)}%` : 'Loading video...'}
            </p>
            
            {/* Progress Bar */}
            <div className="loading-progress-bar">
              <div 
                className="loading-progress-fill" 
                style={{ width: `${Math.max(loadingProgress, 1)}%` }}
              ></div>
            </div>
          </div>
        </div>
      )}
      
      {error && (
        <div className="video-loading-wrapper">
          <div className="video-error">
            <div className="error-icon">⚠️</div>
            <p>{error}</p>
            <button onClick={() => {
              setError(null);
              setLoading(true);
              initializeVideo();
            }}>Try Again</button>
          </div>
        </div>
      )}
      
      <div className="video-wrapper" onClick={handleVideoClick} style={{ display: loading && !isEmbedVideo ? 'none' : 'block' }}>
        <video
          ref={videoRef}
          className="video-element"
          poster={videoProgress?.completed ? undefined : undefined}
          playsInline
          webkit-playsinline="true"
        />

        {showControls && (
          <div className="video-controls">
            <div className="video-info">
              <h3 className="video-title">{videoTitle}</h3>
              {videoDescription && (
                <p className="video-description">{videoDescription}</p>
              )}
            </div>
            
            <div className="controls-row">
              <button 
                className="control-btn play-pause"
                onClick={(e) => {
                  e.stopPropagation();
                  togglePlayPause();
                }}
                aria-label={isPlaying ? 'Pause video' : 'Play video'}
              >
                {isPlaying ? '⏸️' : '▶️'}
              </button>
              
              <div className="progress-container">
                <span className="time-display">{formatTime(currentTime)}</span>
                <input
                  type="range"
                  min="0"
                  max={duration || 0}
                  value={currentTime}
                  onChange={handleSeek}
                  className="progress-bar"
                  aria-label="Video progress"
                />
                <span className="time-display">{formatTime(duration)}</span>
              </div>
              
              <button 
                className="control-btn fullscreen"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFullscreen();
                }}
                aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              >
                {isFullscreen ? '⤓' : '⤢'}
              </button>
            </div>
            
            {videoProgress?.completed && (
              <div className="completion-badge">
                <span>✅ Completed</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoPlayer;
